import { PageLink } from '../components';

import {
  OnboardingResultsAreIn,
  OnboardingCostFactors,
  OnboardingCarePhasesSummary,
  OnboardingFundingCoverage,
  OnboardingFundingSources,
} from '../pages';
import { onboardingSlideByIndex } from './OnboardingSlideInfo';
import { getFirstCarePhasePage } from '../util/navigationLogic';
import { CgShapeCircle } from 'react-icons/cg';
import { CheckIcon } from '@heroicons/react/20/solid';
import { Check } from 'lucide-react';

interface ProgressStepperProps {
  client: Client;
  slideIndex: number;
  show: boolean;
}

export type StepDef = {
  name: 'Review Results' | 'Personalize Plan' | 'Finance Costs';
  beginningSlideIndex: number;
  lastSlideIndex: number;
};

export function getStepDefs(client: Client): StepDef[] {
  const clientFirstCarePhasePage = getFirstCarePhasePage(client);

  return [
    {
      name: 'Review Results',
      beginningSlideIndex: onboardingSlideByIndex.indexOf(
        OnboardingResultsAreIn,
      ),
      lastSlideIndex: onboardingSlideByIndex.indexOf(OnboardingCostFactors),
    },
    {
      name: 'Personalize Plan',
      beginningSlideIndex: onboardingSlideByIndex.indexOf(
        clientFirstCarePhasePage,
      ),
      lastSlideIndex: onboardingSlideByIndex.indexOf(
        OnboardingCarePhasesSummary,
      ),
    },
    {
      name: 'Finance Costs',
      beginningSlideIndex: onboardingSlideByIndex.indexOf(
        OnboardingFundingSources,
      ),
      lastSlideIndex: onboardingSlideByIndex.indexOf(OnboardingFundingCoverage),
    },
  ];
}

export const ProgressStepper: React.FC<ProgressStepperProps> = ({
  client,
  slideIndex,
  show,
}) => {
  if (!show) return null;
  const stepDefs = getStepDefs(client);
  const currentStep = findCurrentStep(stepDefs, slideIndex);

  const renderStep = (step: Step, index: number) => {
    const stepPercentComplete = calcStepPercentComplete(slideIndex, step);
    const isCurrentStep = step === currentStep;
    const isCompleted = stepPercentComplete >= 100 && !isCurrentStep;

    const stepContent = (
      <>
        <ProgressBar percent={isCompleted ? 100 : stepPercentComplete} />
        <div
          className={`flex items-center gap-2 text-xs ${isCurrentStep ? 'text-primary hover:text-primary-hover font-semibold' : 'font-medium text-gray-500'} md:text-sm`}
        >
          {isCompleted && (
            <div className="flex h-5 w-5 items-center justify-center rounded-full bg-green-50 p-1 font-semibold text-green-600">
              <Check
                className="h-4 w-4"
                strokeWidth={4}
              />
            </div>
          )}
          {isCurrentStep && (
            <CgShapeCircle className="hidden h-5 w-5 self-center md:flex" />
          )}
          <span>{step.name}</span>
        </div>
      </>
    );

    return (
      <li
        key={step.name}
        className="flex-1"
      >
        <PageLink
          to={onboardingSlideByIndex[step.beginningSlideIndex]}
          targetProps={{ clientId: client.clientId }}
          className="group flex flex-col"
        >
          {stepContent}
        </PageLink>
      </li>
    );
  };

  return (
    <nav
      aria-label="Progress"
      className="mx-auto w-full max-w-4xl px-2 pb-2 md:px-0"
    >
      <ol className="flex space-x-2 space-y-0 md:space-x-8">
        {stepDefs.map(renderStep)}
      </ol>
    </nav>
  );
};

interface ProgressBarProps {
  percent: number; // Percent of the progress bar to fill
}

export const ProgressBar: React.FC<ProgressBarProps> = ({ percent }) => {
  return (
    <div className="bg-primary-200 mb-2 h-1 w-full rounded-md">
      <div
        className="bg-primary h-1 rounded-md"
        style={{ width: `${percent}%` }}
      ></div>
    </div>
  );
};

function findCurrentStep(steps: Step[], slideNumber: number): Step | undefined {
  return steps.find(
    step =>
      slideNumber >= step.beginningSlideIndex &&
      slideNumber <= step.lastSlideIndex,
  );
}

interface Step {
  name: string;
  beginningSlideIndex: number;
  lastSlideIndex: number;
}

function calcStepPercentComplete(slideIndex: number, step: Step): number {
  if (slideIndex < step.beginningSlideIndex) return 0;
  if (slideIndex > step.lastSlideIndex) return 100;

  return Math.round(
    ((slideIndex - step.beginningSlideIndex) /
      (step.lastSlideIndex - step.beginningSlideIndex + 1)) *
      100,
  );
}
